import { tz } from '@date-fns/tz';
import { format as fnsFormat, parse, parseISO } from 'date-fns';
import parsePhoneNumber from 'libphonenumber-js';

import { ApiSchemas } from '@/components/providers/v1-api';

export function formatPhoneNumber(phoneNumber?: string | null) {
  if (!phoneNumber) return '';
  const phone = parsePhoneNumber(phoneNumber);
  if (phone?.isValid()) return phone.formatNational();
  return phoneNumber;
}

export function formatJson(json: string) {
  try {
    const obj = JSON.parse(json);
    return JSON.stringify(obj, null, 2);
  } catch {
    return json;
  }
}

export function formatAddress(location: ApiSchemas['LocationRead']) {
  let address = '';
  if (location.line1) {
    address += `${location.line1}`;
  }
  if (location.line2) {
    address += `, ${location.line2}`;
  }
  if (location.city) {
    address += `, ${location.city}`;
  }
  if (location.state) {
    address += `, ${location.state} ${location.zip}`.trim();
  }
  return address;
}

export function formatDateTimeInUTC(date: Date | string, format: string) {
  return formatDateTimeInTimezone({ date, format, timezone: 'UTC' });
}

export function formatDateTimeInTimezone({
  date,
  format,
  timezone,
}: {
  date: Date | string;
  format: string;
  timezone: string;
}) {
  if (typeof date === 'string') {
    const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(date);
    const d = parseISO(isDateOnly ? `${date}T00:00:00.000Z` : date);
    return fnsFormat(d, format, { in: tz(timezone) });
  }
  return fnsFormat(date, format, { in: tz(timezone) });
}

/**
 * Short timezone abbreviation for an instant in a given IANA timezone
 * (e.g. "CST"/"CDT" for "America/Chicago"). DST-correct via Intl.
 */
export function formatShortTimezone(date: Date | string, timezone: string): string {
  const d = typeof date === 'string' ? parseISO(date) : date;
  const part = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'short',
  })
    .formatToParts(d)
    .find((p) => p.type === 'timeZoneName');
  return part?.value ?? '';
}

export function formatDurationFromMillis(value: number, unit: 'sec' | 'min' = 'sec') {
  if (!Number.isFinite(value) || value < 0) return '';
  const totalMillis = Math.max(0, Math.floor(value));
  const totalSeconds = Math.floor(totalMillis / 1000);

  if (unit === 'sec') {
    // Render total seconds with unit
    return `${totalSeconds} s`;
  }

  // unit === 'min' -> render total minutes with 2 decimal places
  const totalMinutes = (totalSeconds / 60).toFixed(2);
  return `${totalMinutes} m`;
}

export function formatMetersAsMiles(meters: number) {
  return `${(meters * 0.000621371).toFixed(1)}`;
}

export function formatTravelTime(seconds: number): string {
  const minutes = Math.round(seconds / 60);
  // round up to next 5-minute increment
  const ceiledTo5 = Math.ceil(minutes / 5) * 5;
  return `${ceiledTo5} min`;
}

export function formatCustomerName(customer: {
  company?: string | null;
  first_name?: string | null;
  last_name?: string | null;
}) {
  if (customer.company && customer.company !== '') {
    return customer.company;
  }
  const name = `${customer.first_name ?? ''} ${customer.last_name ?? ''}`.trim();
  if (name === '') {
    return 'No name';
  }
  return name;
}

export function formatCentsAsPrice(cents: number | null | undefined, currency = 'USD'): string {
  if (cents === null || cents === undefined) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(cents / 100);
}

/**
 * Formats a time string (HH:mm or HH:mm:ss) to 12-hour format.
 * Omits minutes when they are 00 (e.g., "8 AM" instead of "8:00 AM").
 */
export function formatTime12Hour(time: string): string {
  const inFmt = time.split(':').length === 2 ? 'HH:mm' : 'HH:mm:ss';
  const d = parse(time, inFmt, new Date());
  const minutes = d.getMinutes();
  return minutes === 0 ? fnsFormat(d, 'h a') : fnsFormat(d, 'h:mm a');
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const DOCUMENT_TYPE_OPTIONS: { value: ApiSchemas['DocumentType']; label: string }[] = [
  { value: 'DOCUMENT', label: 'Document' },
  { value: 'CONTRACT', label: 'Contract' },
  { value: 'FIELD_REPORT', label: 'Field Report' },
  { value: 'INVOICE', label: 'Invoice' },
];

export function formatDocumentType(type: ApiSchemas['DocumentType']): string {
  return DOCUMENT_TYPE_OPTIONS.find((opt) => opt.value === type)?.label || type;
}
